import * as Sentry from "@sentry/react";

const DSN = import.meta.env.VITE_SENTRY_DSN;
const environment =
  import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE;
const isProduction = environment === "production";
const isDevelopment = import.meta.env.MODE === "development" || import.meta.env.DEV;

const tracesSampleRate = Number(
  import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? (isProduction ? 0.1 : 1.0)
);
const replaysSessionSampleRate = Number(
  import.meta.env.VITE_SENTRY_REPLAYS_SAMPLE_RATE ?? (isProduction ? 0 : 0.1)
);
const replaysOnErrorSampleRate = Number(
  import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE ?? 1.0
);

const piiKeys = ["name", "email", "phone", "address", "token", "session", "birth", "dob"];

const redactValue = (value: unknown) =>
  typeof value === "string" ? "[REDACTED]" : value;

const scrubObject = (input: unknown): unknown => {
  if (!input || typeof input !== "object") return input;
  if (Array.isArray(input)) {
    return input.map((item) => scrubObject(item));
  }
  const clone: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(input)) {
    const lowered = key.toLowerCase();
    if (piiKeys.some((needle) => lowered.includes(needle))) {
      clone[key] = redactValue(value);
      continue;
    }
    clone[key] =
      typeof value === "object" ? scrubObject(value) : value;
  }
  return clone;
};

const scrubEvent = (event: Sentry.Event): Sentry.Event => {
  if (!event) return event;

  // Drop user to avoid attaching identity/PII
  event.user = undefined;

  if (event.request) {
    event.request = {
      url: event.request.url,
      method: event.request.method,
    };
  }

  if (event.contexts) {
    event.contexts = scrubObject(event.contexts) as Sentry.Event["contexts"];
  }

  if (event.extra) {
    event.extra = scrubObject(event.extra) as Sentry.Event["extra"];
  }

  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map((breadcrumb) => ({
      ...breadcrumb,
      data: scrubObject(breadcrumb.data) as Record<string, unknown> | undefined,
    }));
  }

  return event;
};

if (DSN && !isDevelopment) {
  Sentry.init({
    dsn: DSN,
    environment,
    sendDefaultPii: false,
    enableLogs: true,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
      Sentry.consoleLoggingIntegration({ levels: ["warn", "error"] }),
    ],
    tracesSampleRate,
    tracePropagationTargets: ["localhost", /^https:\/\/.*\.supabase\.co/],
    replaysSessionSampleRate,
    replaysOnErrorSampleRate,
    beforeSend(event: Sentry.ErrorEvent) {
      return scrubEvent(event) as Sentry.ErrorEvent;
    },
  });
} else if (isDevelopment) {
  console.info("[sentry] Skipped in development mode");
} else {
  console.warn("[sentry] DSN missing; Sentry is not initialized.");
}

export { Sentry };
export const { logger } = Sentry;

export function captureException(
  error: unknown,
  context?: Record<string, unknown>
): void {
  let errorToCapture: Error;

  if (error instanceof Error) {
    errorToCapture = error;
  } else if (error && typeof error === "object") {
    // Supabase errors are plain objects with: code, details, hint, message
    const obj = error as Record<string, unknown>;
    const message =
      (obj.message as string) ||
      (obj.details as string) ||
      "Unknown error";
    errorToCapture = new Error(message);
    // Preserve original object data for debugging
    context = { ...context, supabaseError: obj };
  } else {
    errorToCapture = new Error(String(error));
  }

  Sentry.captureException(errorToCapture, { extra: context });
}
